import React from 'react';
import { motion } from 'framer-motion';
import { BrainIcon, HeartIcon, ImageIcon, LayersIcon } from 'lucide-react';
import { masterSnaks } from '../../data/masterSnaks';
import { InteriorSlide, SlideProps } from './SlideShell';
const LIVE_SNAK_IMAGE = 'https://cdn.magicpatterns.com/uploads/dNq8aCHoPXQJWqBhZ1A5aB/Screenshot_2026-09-16_at_13.18.24.png';
const COLUMNS = [{
  label: 'Snak',
  icon: LayersIcon
}, {
  label: 'Cognitive anchor',
  icon: BrainIcon
}, {
  label: 'Emotional payoff',
  icon: HeartIcon
}, {
  label: 'Core deck visual (animation source)',
  icon: ImageIcon
}] as const;
const ROW_GRID = 'grid-cols-[0.8fr_0.95fr_0.95fr_2.3fr]';
export function SlideMasterSnaks({}: SlideProps) {
  return <InteriorSlide surface data-id="element-388" data-mp="35ot57pnk3y35">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.7vw,1.5rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-389" data-mp="715hunv7ra2v">
        <div data-id="element-390" data-mp="1o7ljq6tnv375">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-391" data-mp="1o963r2icn3qr">
            Master Snak roadmap
          </p>
          <h2 className="text-[clamp(2rem,3.3vw,3.2rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-392" data-mp="1fdqfd10d5gsk">
            Suggested further{' '}
            <span className="text-[var(--brand)]" data-id="element-393" data-mp="15glt716iplbr">Master Snaks</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-394" data-mp="1af5fj2p0wz4n">
          Each Snak anchors one cognitive idea and one emotional payoff, built
          from a visual that already exists in the approved core deck.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-395" data-mp="2qtosbfynwmz2">
        <motion.section initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16,
        duration: 0.6
      }} className="min-h-0 overflow-hidden rounded-3xl border border-[var(--brand)]/10 bg-white shadow-sm lg:col-span-9" aria-label="Suggested further Master Snaks with cognitive anchor, emotional payoff and core deck visual" data-id="element-396" data-mp="2qrkea571u7l2">
          <div className={`grid ${ROW_GRID} bg-[var(--brand)] px-5 py-3.5 text-white`} data-id="element-397" data-mp="3utdcgutmw6pt">
            {COLUMNS.map(column => {
            const Icon = column.icon;
            return <div key={column.label} className="flex items-center gap-2 pr-3" data-id="element-398" data-mp="mbz26sktbgby">
                  <Icon className="h-3.5 w-3.5 flex-shrink-0 text-white/70" aria-hidden="true" data-id="element-399" data-mp="me35b8aw0bb6" />
                  <p className="text-[10px] font-bold uppercase leading-tight tracking-[0.1em]" data-id="element-400" data-mp="cl4dlhk3g4nc">
                    {column.label}
                  </p>
                </div>;
          })}
          </div>

          <div className="flex h-[calc(100%-2.75rem)] flex-col divide-y divide-[var(--brand)]/10" data-id="element-401" data-mp="1599z46iz1xo8">
            {masterSnaks.map((snak, index) => <motion.article key={snak.name} initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.28 + index * 0.08,
            duration: 0.42
          }} className={`grid min-h-0 flex-1 ${ROW_GRID} items-center px-5 py-2.5`} data-id="element-402" data-mp="1hkas1qc7v1tx" data-mpi={index}>
                <p className="pr-4 text-xs font-extrabold leading-snug text-[var(--fg)]" data-id="element-403" data-mp="1vk2bi6li5afb">
                  {snak.name}
                </p>
                <p className="pr-4 text-xs font-semibold leading-snug text-[var(--brand)]" data-id="element-404" data-mp="25aes5nq74jg6">
                  {snak.cognitiveAnchor}
                </p>
                <p className="pr-4 text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-405" data-mp="2a86nkrq1unp4">
                  {snak.emotionalPayoff}
                </p>
                <div className="pr-1" data-id="element-406" data-mp="2ja65dm8w1vt4">
                  <p className="text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-407" data-mp="2jaxzesmu7lha">
                    {snak.coreVisual}{' '}
                    <span className="font-bold text-[var(--muted)]" data-id="element-408" data-mp="25lcqnfka0l18">
                      ({snak.deckPage})
                    </span>
                  </p>
                  <p className="mt-1 inline-block rounded-full bg-[var(--surface)] px-2 py-0.5 text-[9px] font-semibold leading-tight text-[var(--muted)]" data-id="element-409" data-mp="2fdikyvy03ejl">
                    {snak.source}
                  </p>
                </div>
              </motion.article>)}
          </div>
        </motion.section>

        <motion.aside initial={{
        opacity: 0,
        x: 18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex min-h-0 flex-col lg:col-span-3" data-id="element-410" data-mp="1iob4418bttm0">
          <div className="mb-3" data-id="element-411" data-mp="1rpr7vq4myxh9">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-412" data-mp="381enwie5y2ot">
              Live example
            </p>
            <h3 className="mt-1 text-lg font-extrabold text-[var(--fg)]" data-id="element-413" data-mp="332bgirapnkdo">
              Interactive Snak
            </h3>
          </div>

          <figure className="relative min-h-0 flex-1 overflow-hidden rounded-[1.75rem] bg-[#1F4A33] shadow-[0_18px_45px_rgba(23,32,51,0.18)] ring-1 ring-black/5" data-id="element-414" data-mp="tu5bsghb0m2m">
            <img src={LIVE_SNAK_IMAGE} alt="Columvi Snak asking what you would expect to see after three years of follow up in 2L DLBCL if a treatment truly has curative potential, with three answer options" className="h-full w-full object-contain" data-id="element-415" data-mp="fvymhoqf7uek" />
          </figure>
        </motion.aside>
      </div>
    </InteriorSlide>;
}